import React, { useState } from 'react';
import { Accordion } from '../accordion';
import { useTheme } from '../../providers/theme';
import { getColors } from '../../components/utils/getColors';
import { ZoomInOutlined, ZoomOutOutlined, ExpandOutlined, CompressOutlined } from '@ant-design/icons';
import { Tooltip, Flex } from 'antd';
import { Button } from '../button';
import { useSelected } from '../../providers/selectedContext';
import DashboardChart from '../../components/chart/chart';
import { ChartData } from '../../@types/data/types';
import { AnalyzeType } from '../../@types/samples/types';

interface LiaoResultsProps {
  liaoChartData: ChartData;
  isGeneratingPDF: boolean;
}

const heights = ['46%', '50%', '54%', '60%', '70%', '80%', '90%'];
const widths = ['49%', '100%'];

const LiaoResults = (props: LiaoResultsProps) => {
  const { selectedInfo } = useSelected();
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const hasLiaoData = !!selectedInfo?.sample?.analysis_values?.liao;
  const hasLambdaData = !!selectedInfo?.sample?.analysis_values?.lambda_values;
  const [chartHeight, setChartHeight] = useState(heights[1]);
  const [containerWidth, setContainerWidth] = useState('49%');

  if (!hasLiaoData && !hasLambdaData) {
    return (
      <Accordion defaultOpen="liao-results">
        <Accordion.Item id="liao-results" header="Liao Results">
          <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.7, color: colors.text }}>
            No analysis data available. Go to <strong>Sample Measurements</strong> to add Sw and Pc values and click Calculate.
          </p>
        </Accordion.Item>
      </Accordion>
    );
  }

  const lineData = [{ x: 0, y: 0 }, { x: 1, y: 1 }];

  const handleExpand = () => {
    const i = heights.indexOf(chartHeight);
    if (i < heights.length - 1) setChartHeight(heights[i + 1]);
  };
  const handleCompress = () => {
    const i = heights.indexOf(chartHeight);
    if (i > 0) setChartHeight(heights[i - 1]);
  };
  const handleZoomIn = () => {
    const i = widths.indexOf(containerWidth);
    if (i < widths.length - 1) setContainerWidth(widths[i + 1]);
  };
  const handleZoomOut = () => {
    const i = widths.indexOf(containerWidth);
    if (i > 0) setContainerWidth(widths[i - 1]);
  };

  return (
    <Accordion defaultOpen="liao-results">
      <Accordion.Item id="liao-results" header="Liao Results">
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Tooltip title="Zoom in charts">
              <Button variant="ghost" size="sm" onClick={handleZoomIn}>
                <ZoomInOutlined />
              </Button>
            </Tooltip>
            <Tooltip title="Zoom out charts">
              <Button variant="ghost" size="sm" onClick={handleZoomOut}>
                <ZoomOutOutlined />
              </Button>
            </Tooltip>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Tooltip title="Expand charts">
              <Button variant="ghost" size="sm" onClick={handleExpand}>
                <ExpandOutlined />
              </Button>
            </Tooltip>
            <Tooltip title="Compress charts">
              <Button variant="ghost" size="sm" onClick={handleCompress}>
                <CompressOutlined />
              </Button>
            </Tooltip>
          </div>
        </div>
        <Flex wrap="wrap" gap="small" justify="space-between" style={{ marginTop: '1rem' }}>
          <DashboardChart
            liaoChartData={props.liaoChartData.line}
            receivedLiao={lineData}
            name="Finding 'a' and 'b' values "
            xAxis="X"
            yAxis="Y"
            isGenerating={props.isGeneratingPDF}
            sampleCode={selectedInfo.sample?.sample_code}
            height={chartHeight}
            containerWidth={containerWidth}
            analysisType={AnalyzeType.LIAO}
          />
          <DashboardChart
            liaoChartData={props.liaoChartData.pcdxswd}
            receivedLiao={props.liaoChartData.new_pcdxswd}
            name="Primary pc curve"
            xAxis="SwD"
            yAxis="PcD"
            isGenerating={props.isGeneratingPDF}
            sampleCode={selectedInfo.sample?.sample_code}
            height={chartHeight}
            containerWidth={containerWidth}
            analysisType={AnalyzeType.LIAO}
          />
        </Flex>
      </Accordion.Item>
    </Accordion>
  );
};

export default LiaoResults;
